import React, { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../components/Breadcrumbs";
import QuestionCard from "../components/QuestionCard";
import { useNavigate, useParams } from "react-router-dom";
import { profilesIdTopicsQuestions_req, topicAnswered_req, userActivity_req } from "../api/profiles.api";
import Button from "../components/Buttons/button";
import ProfileUnavailable from "./profile-unavailable";
import { useDispatch, useSelector } from "react-redux";
import { openModals } from "../redux/modal.slice";
import ModalsEnum from "../enums/modal.enum";
import { setAnswersData, setMeData } from "../redux/me.slice";
import { savePendingSurvey } from "../utils/pendingSurvey";
import { removeAuthToken } from "../utils/authToken";
import { toFiniteNumber } from "../utils/number";

const Questions = ({ seconds }) => {
  const { profileId, topicId, "*": rest } = useParams();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.me?.data);
  const sessionId = useSelector(state => state.me.sessionData);

  const segments = useMemo(() => (rest || "").split("/").filter(Boolean), [rest]);
  const subtopicIds = useMemo(() => segments.filter(s => s !== "subtopics" && s !== "questions").map(Number), [segments]);
  const leafId = subtopicIds.length > 0 ? subtopicIds[subtopicIds.length - 1] : Number(topicId);

  const [data, setData] = useState(null);
  const [questionsData, setQuestionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [dropValues, setDropValues] = useState({});
  const [inputsComplete, setInputsComplete] = useState(false);

  const handleInputChange = (id, value, min, max) => {
    setInputValues(prev => ({ ...prev, [id]: value }));
  };

  const handleDropChange = (id, value) => {
    setDropValues(prev => ({ ...prev, [id]: value }));
  };

  useEffect(() => {
    const questions = data?.topic_questions;

    if (!questions || questions.length === 0) {
      setInputsComplete(false);
      return;
    }

    // Every question is required on the frontend regardless of the backend
    // "required" flag, so Generate Report only enables once all fields hold
    // a real answer (placeholder "Select an option" never counts).
    const isValid = questions.every(question => {
      const { id, question_type, min_value, max_value } = question;

      if (question_type === "dropdown") {
        const value = dropValues[id];
        return value !== undefined && value !== null && value !== "";
      }

      const value = inputValues[id];

      if (value === undefined || value === null || value === "") {
        return false;
      }

      const min = toFiniteNumber(min_value);
      const max = toFiniteNumber(max_value);

      if (min !== null || max !== null) {
        const num = toFiniteNumber(value);
        if (num === null) return false;
        if (min !== null && num < min) return false;
        if (max !== null && num > max) return false;
      }

      return true;
    });

    setInputsComplete(isValid);
  }, [inputValues, dropValues, data?.topic_questions]);

  useEffect(() => {
    let cancelled = false;

    const fetchQuestions = async () => {
      setLoading(true);
      setData(null);
      setProfileMissing(false);

      try {
        const response = await profilesIdTopicsQuestions_req(profileId, leafId);

        if (cancelled) return;

        setQuestionsData(response?.result?.topic);

        // Reset answers for the newly loaded question set; dropdowns start
        // unanswered (placeholder "Select an option") rather than pre-filled.
        setInputValues({});
        setDropValues({});
        setData(response?.result);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;

        if (error?.response?.status === 404) {
          setProfileMissing(true);
        }
        setLoading(false);
      }
    };

    fetchQuestions();

    return () => {
      cancelled = true;
    };
  }, [profileId, leafId]);

  const generateReport = async () => {
    const questionsList = data?.topic_questions || [];

    // Build one answer per question (never a placeholder "" value), so a
    // dropdown that only "looks" selected can never be sent unanswered.
    const answers = questionsList
      .map(question => {
        const value = question.question_type === "dropdown" ? dropValues[question.id] : inputValues[question.id];

        if (value === undefined || value === null || value === "") {
          return null;
        }

        return {
          question_version_history_id: Number(question.question_version_history_id),
          value,
        };
      })
      .filter(Boolean);
    console.log("Answers Payload:", answers);

    if (user) {
      try {
        const response = await topicAnswered_req(profileId, leafId, answers);
        if (response) {
          localStorage.setItem("profileId", profileId);
          localStorage.setItem("topicId", String(leafId));

          await userActivity_req(sessionId, seconds, "questions_screen");
          navigation(`/report/${profileId}?topic=${leafId}`, {
            state: { data: response },
          });
        }
      } catch (e) {
        // Token expired mid-survey: don't lose the answers. Fall back to the
        // same pending-survey + sign-in resume flow used for logged-out users.
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          removeAuthToken();
          dispatch(setMeData(null));
          savePendingSurvey(profileId, leafId, answers);
          dispatch(setAnswersData(answers));
          dispatch(
            openModals({
              modalIds: [ModalsEnum.SIGN_IN_MODAL],
            }),
          );
        }
      }
    } else {
      savePendingSurvey(profileId, leafId, answers);
      dispatch(setAnswersData(answers));
      dispatch(
        openModals({
          modalIds: [ModalsEnum.SIGN_IN_MODAL],
        }),
      );
    }
  };

  if (profileMissing) {
    return <ProfileUnavailable />;
  }
  return (
    <div className="questions">
      <div className="container">
        <Breadcrumbs />
        <div className="questions__head text-center">
          <div className="questions__icon">{questionsData?.image_url && <img src={questionsData.image_url} alt={questionsData.name || ""} />}</div>
          <h2 className="mb-20">{questionsData?.name}</h2>
          <p className="mb-80 text-color-grey">{questionsData?.description}</p>
        </div>
        {loading ? (
          <p className="loading-text text-center">Analyzing your responses and preparing your insights.</p>
        ) : (
          <div className="questions__content">
            {data?.topic_questions?.map(item => (
              <QuestionCard key={item.id} id={item.id} question={item.question_text} dropdown={item.options} type={item.question_type} inputValue={inputValues[item.id] ?? ""} setInputValue={val => handleInputChange(item.id, val, item.min_value, item.max_value)} dropValue={dropValues[item.id] ?? ""} setDropValue={val => handleDropChange(item.id, val)} minValue={item.min_value} maxValue={item.max_value} />
            ))}
          </div>
        )}

        <Button text={"Generate Report"} fullWidth={true} onClick={generateReport} disabled={!inputsComplete} />
      </div>
    </div>
  );
};

export default Questions;
